import type { Edulead } from 'edulead';

/**
 * A person of the Go API as the person components read it: the field names and nesting of the
 * legacy search index document (`edulead-contacts-035`, `/v2/people/search`'s `data[]` and
 * `/v2/people/:id`'s `data`). CustomList, the keystone row and the person page's components read
 * that document, so the API's shape is translated here, once, rather than in each of them.
 * Fields the 035 document never carries (`titleHistory`, `emailDomainCount`, a role's
 * `institution`) are left out, as they were.
 */
interface LegacyPerson {
  id: string;
  name: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  title: string | null;
  titleRank: number | null;
  email: string | null;
  emailDomain: string | null;
  emailType: string | null;
  emailFormat: string | null;
  emailSource: string | null;
  emailVerificationStatus: string | null;
  emailVerifiedAt: string | null;
  phone: string | null;
  phoneUniqueness: string | null;
  state: string | null;
  accountId: string | null;
  accountPriority: number;
  accountNrStudents: number;
  priority: number;
  labels: string[];
  bios: { bio: string; created_at: string | null }[];
  belongsTo: { institution: LegacyPersonInstitution }[];
  roles: LegacyRole[];
  attributionEarliest: string | null;
  attributionMostRecent: string | null;
  importedAt: string | null;
  fulltextMatchedUrls?: { url: string; snippet: string }[];
}

interface LegacyPersonInstitution {
  id: string;
  name: string;
  type: string;
  subtype: string | null;
  access: string | null;
  loc_state: string | null;
  loc_zip: string | null;
  loc_county_id: string | null;
  loc_address_phys: string | null;
  loc_city: string | null;
  id_nces: string | null;
  id_nces_district: string | null;
  nr_students: number;
  nr_staff: number;
  revenue_total: number;
}

interface LegacyRole {
  roleCode: string | null;
  role: string | null;
  area: string | null;
  modifier: string | null;
  status: string | null;
  level: string | null;
  systemStatus: string | null;
  confidence: number | null;
  rank: number | null;
  decisionLevel: string | null;
  functionalGroup: string | null;
  marketThemes: string[];
  evidenceCount: number;
  firstObservedAt: string | null;
  lastObservedAt: string | null;
  evidence: LegacyEvidence[];
}

interface LegacyEvidence {
  id: string;
  normalizedTitle: string | null;
  sourceType: string | null;
  matchMethod: string | null;
  status: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  observedAt: string | null;
  observations:
    { institutionId: string; resolutionMethod: string; resolutionValue: string }[] | null;
  url: {
    urlId: string;
    url: string;
    urlType: string | null;
    urlStatus: string | null;
    screenshotKey: string | null;
  } | null;
}

/** The index version CustomList and the person page read to choose the keystone components. */
export const KEYSTONE_VERSION = 35;

/** A search page in the legacy envelope CustomList reads (`data.data`, `data.total`, …). */
export interface LegacyPeopleResponse {
  data: {
    data: LegacyPerson[];
    total: number;
    version: number;
    aggregations?: { uniqueDistricts: number };
  };
}

/** An account of a grouped search, as CustomList and Buying Groups read it. */
interface LegacyAccountBucket {
  accountId: string;
  accountName: string;
  accountPriority: number;
  contactCount: number;
  contacts: LegacyPerson[];
}

/** A grouped search page in the legacy envelope Buying Groups reads. */
export interface LegacyGroupedPeopleResponse {
  data: {
    data: LegacyAccountBucket[];
    total: number;
    totalContacts: number;
    version: number;
    districtSizeHistogram?: { min: number; max: number | null; count: number }[];
  };
}

/** An absent or empty string as the document's null. */
const str = (s: string | undefined) => s || null;

/** An int64 of the API (a JSON string) as the number the components format. */
const int = (s: string | undefined) => Number(s ?? 0);

function toLegacyEvidence(e: Edulead.EduleadV4PersonEvidence): LegacyEvidence {
  return {
    id: e.id ?? '',
    normalizedTitle: str(e.normalized_title),
    sourceType: str(e.source_type),
    matchMethod: str(e.match_method),
    status: str(e.status),
    firstName: str(e.first_name),
    middleName: str(e.middle_name),
    lastName: str(e.last_name),
    observedAt: str(e.observe_time),
    observations: e.observations?.length
      ? e.observations.map((o) => ({
          institutionId: o.institution_id ?? '',
          resolutionMethod: o.resolution_method ?? '',
          resolutionValue: o.resolution_value ?? '',
        }))
      : null,
    url: e.url
      ? {
          urlId: e.url.url_id ?? '',
          url: e.url.url ?? '',
          urlType: str(e.url.url_type),
          urlStatus: str(e.url.url_status),
          screenshotKey: str(e.url.screenshot_key),
        }
      : null,
  };
}

function toLegacyRole(r: Edulead.EduleadV4PersonRole): LegacyRole {
  return {
    roleCode: str(r.code),
    role: str(r.role),
    area: str(r.area),
    modifier: str(r.modifier),
    status: str(r.status),
    level: str(r.level),
    systemStatus: str(r.system_status),
    confidence: r.confidence ?? null,
    rank: r.rank ?? null,
    decisionLevel: str(r.decision_level),
    functionalGroup: str(r.functional_group),
    marketThemes: r.market_themes ?? [],
    evidenceCount: r.evidence_count ?? 0,
    firstObservedAt: str(r.first_observe_time),
    lastObservedAt: str(r.last_observe_time),
    evidence: (r.evidence ?? []).map(toLegacyEvidence),
  };
}

function toLegacyInstitution(i: Edulead.EduleadV4PersonInstitution): LegacyPersonInstitution {
  return {
    id: i.institution_id ?? '',
    name: i.name ?? '',
    type: i.type ?? '',
    subtype: str(i.subtype),
    access: str(i.access),
    loc_state: str(i.state),
    loc_zip: str(i.zip),
    loc_county_id: str(i.county_id),
    loc_address_phys: str(i.address),
    loc_city: str(i.city),
    id_nces: str(i.nces_id),
    id_nces_district: str(i.nces_district_id),
    nr_students: int(i.student_count),
    nr_staff: int(i.staff_count),
    revenue_total: int(i.revenue_usd),
  };
}

export function toLegacyPerson(p: Edulead.EduleadV4Person): LegacyPerson {
  return {
    id: p.id ?? '',
    name: str(p.name),
    firstName: str(p.first_name),
    middleName: str(p.middle_name),
    lastName: str(p.last_name),
    title: str(p.title),
    titleRank: p.title_rank ?? null,
    email: str(p.email),
    emailDomain: str(p.email_domain),
    emailType: str(p.email_type),
    emailFormat: str(p.email_format),
    emailSource: str(p.email_source),
    emailVerificationStatus: str(p.email_verification_status),
    emailVerifiedAt: str(p.email_verify_time),
    phone: str(p.phone),
    phoneUniqueness: str(p.phone_uniqueness),
    state: str(p.state),
    accountId: str(p.account_id),
    accountPriority: p.account_priority ?? 0,
    accountNrStudents: int(p.account_student_count),
    priority: p.priority ?? 0,
    labels: p.labels ?? [],
    bios: (p.bios ?? []).map((b) => ({ bio: b.text ?? '', created_at: str(b.create_time) })),
    belongsTo: (p.institutions ?? []).map((i) => ({ institution: toLegacyInstitution(i) })),
    roles: (p.roles ?? []).map(toLegacyRole),
    attributionEarliest: str(p.first_observe_time),
    attributionMostRecent: str(p.last_observe_time),
    importedAt: str(p.update_time),
    fulltextMatchedUrls: p.fulltext_matches?.length
      ? p.fulltext_matches.map((m) => ({ url: m.url ?? '', snippet: m.snippet ?? '' }))
      : undefined,
  };
}

/**
 * A page of search results in the legacy envelope: the total over every page, and the distinct
 * districts as legacy's `aggregations.uniqueDistricts` (set only when there are some, as legacy).
 */
export function legacyPeopleResponse(
  items: Edulead.EduleadV4Person[],
  response: Edulead.EduleadV4SearchPeopleResponse,
): LegacyPeopleResponse {
  const districts = int(response.district_count);
  return {
    data: {
      data: items.map(toLegacyPerson),
      total: int(response.total_size),
      version: KEYSTONE_VERSION,
      ...(districts > 0 ? { aggregations: { uniqueDistricts: districts } } : {}),
    },
  };
}

/** A grouped search page (`group_by: 'account'`) in the legacy envelope Buying Groups reads. */
export function legacyGroupedPeopleResponse(
  response: Edulead.EduleadV4SearchPeopleResponse,
): LegacyGroupedPeopleResponse {
  const histogram = response.account_size_histogram;
  return {
    data: {
      data: (response.groups ?? []).map((g) => ({
        accountId: g.account_id ?? '',
        accountName: g.name ?? '',
        accountPriority: g.priority ?? 0,
        contactCount: int(g.person_count),
        contacts: (g.people ?? []).map(toLegacyPerson),
      })),
      total: int(response.total_size),
      totalContacts: int(response.person_total),
      version: KEYSTONE_VERSION,
      ...(histogram?.length
        ? {
            districtSizeHistogram: histogram.map((b) => ({
              min: int(b.min),
              max: b.max ? int(b.max) : null,
              count: int(b.account_count),
            })),
          }
        : {}),
    },
  };
}

/**
 * The Go API's order for the console's sort (the URL's `sortBy` and `sortDir`): name or email,
 * the two sortable columns, either way; `priority` (an institution's people) most first; anything
 * else is relevance.
 */
export function personOrder(
  sortBy: string | undefined,
  sortDir: string | undefined,
): Edulead.SearchPeopleRequestOrderBy {
  const desc = sortDir === 'desc' ? ' desc' : '';
  switch (sortBy) {
    case 'name':
      return `name${desc}` as 'name' | 'name desc';
    case 'email':
      return `email${desc}` as 'email' | 'email desc';
    case 'priority':
      return 'priority desc';
    default:
      return 'relevance';
  }
}
